import axios from 'axios';
import store from '@/store';
import router from '@/router';
import popups from '@/plugins/popups/popups';
import tolgee from '@/tolgee.js';

const timeout = 60000;

axios.defaults.withCredentials = true;

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout,
  headers: {
    'Content-Type': 'application/json'
  }
});

let loaderTimeout;

instance.interceptors.request.use(
  (config) => {
    // `silent: true` opts a request out of the global blocking loader — used by
    // background/optimistic requests that already update the UI instantly.
    if (!config.silent && !loaderTimeout) loaderTimeout = setTimeout(() => store.commit('showLoader', true), 1000);
    const access = localStorage.getItem('accessToken');
    if (access) config.headers.auth = `Bearer ${access}`;
    if (store.state.role) config.headers.role_id = store.state.role;
    if (store.state.subjectId) config.headers.subject_id = store.state.subjectId;
    return config;
  },

  (error) => {
    clearTimeout(loaderTimeout);
    store.commit('showLoader', false);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    clearTimeout(loaderTimeout);
    store.commit('showLoader', false);
    if (response.status == 202)
      popups.popupSuccess(tolgee.t(response.data?.message), tolgee.t('instance.modal.success.title'), 'verified');
    // Only `/notifications/count` ships `popups` — the page-load push. The
    // drawer's `notifications` list is deliberately NOT read here: popping its
    // rows stacked one modal per row over the drawer already showing them.
    // However many there are, they open as ONE modal.
    if (response.data?.popups?.length && store.state.role != 'superadmin') {
      popups.popupNotifications({ notifications: response.data.popups });
    }

    return response;
  },

  async (error) => {
    clearTimeout(loaderTimeout);
    store.commit('showLoader', false);

    if (error.response?.status == 403) router.push('/confirm');
    else if (error.response?.status == 401 && error.config) {
      try {
        const {
          data: { accessToken }
        } = await axios.get(import.meta.env.VITE_API_URL + '/refresh', { withCredentials: true });
        localStorage.setItem('accessToken', accessToken);
        return instance.request(error.config);
      } catch (err) {
        localStorage.removeItem('accessToken');
        router.push({ name: 'route-signin' });
      }
      throw error;
    } else if (!error.config?.quiet) {
      // `quiet: true` opts a request out of the blocking error modal — for calls whose
      // failure the caller renders inline (e.g. an unavailable preview). The promise
      // still rejects, so the caller must handle it.
      const { message, header, icon, args } = error.response?.data ?? {};
      console.log(error.response?.data);
      const msg = tolgee.t(`error.${message}.text`, args ?? {});
      const msgSuffix = `
        <div style='margin-top:1.5em'>
          <a class='link' href="javascript:window.history.back()">${tolgee.t('instance.modal.error.back')}</a>
          &ensp;&bull;&ensp;
          <a class='link' href="javascript:location.reload()">${tolgee.t('instance.modal.error.refresh')}</a>
          &ensp;&bull;&ensp;
          <a class='link' href="/">${tolgee.t('instance.modal.error.home')}</a>
        </div>
        <div class="separator" style='margin:1.5em 0'></div>
        <div style='margin-top:1.5em'>
          <span>${tolgee.t('instance.modal.error.contact_us')}</span>
          <a class='link' href='mailto:education@gen.tech' target='_blank'>education@gen.tech</a>
        </div>`;
      popups.popupError(
        !msg ? `${tolgee.t('instance.modal.error.timeout')} ${msgSuffix}` : msg + msgSuffix,
        tolgee.t(`error.${header}.title` ?? 'instance.modal.error.title'),
        icon || 'cancel'
      );
    }

    return Promise.reject({ ...error });
  }
);

export default instance;
